import type { FallbackProps } from '@gik/core/api/BaseAPIConfig';
import { withTitle } from '@gik/core/hocs/withTitle';
import routes from '@gik/core/routes';
import { getAssetsUrl } from '@gik/core/utils/LinkUtils';
import type { IHomePageProps } from '@gik/landing-pages/pages/HomePage';
import type { ILandingPageProps } from '@gik/landing-pages/pages/LandingPage';
import { LandingPage } from '@gik/landing-pages/pages/LandingPage';
import type { PageLayoutProps } from '@/components/layout/PageLayout';
import { PageLayout } from '@/components/layout/PageLayout';
import { SWRConfig } from 'swr';

export default function LandingPageTemplateComp({ fallback }: FallbackProps<IHomePageProps>) {
  return withTitle(
    'Connected Fundraising',
    <SWRConfig value={{ fallback }}>
      <LandingPage {...landingPageProps} url="/free-fundraising" />
    </SWRConfig>
  );
}

LandingPageTemplateComp.getLayout = function getLayout(page, layoutProps: PageLayoutProps) {
  return (
    <PageLayout {...layoutProps} key={layoutProps.key}>
      {page}
    </PageLayout>
  );
};

// FIXME: Replace this strategy with i18n library instead
// @ts-ignore
const landingPageProps: ILandingPageProps = {
  heroSection: {
    hero: {
      title: 'Fundraise Your Way',
      content: 'Give InKind makes fundraising easy and flexible, all from one centralized platform.',
    },
    backgroundImage: `${getAssetsUrl()}landing-pages/headers/hero-friends-mobile@2x.png`,
    backgroundImageMd: `${getAssetsUrl()}landing-pages/headers/hero-friends-desktop@2x.png`,
    backgroundColor: '#f6ffef',
  },
  featureFocus: {
    feature: {
      title: 'Why Choose Give InKind?',
      icon: `${getAssetsUrl()}landing-pages/fundraising-blog-thumbnail.png`,
      features: [
        <>
          <span className={'tw-font-bold'}>Connect Your Preferred Fundraiser, No Extra Fees:</span> Easily link your
          favorite fundraising platform to your page without additional charges or hidden costs.
        </>,
        <>
          <span className={'tw-font-bold'}>Flexible Payment Options:</span> Accept donations seamlessly via Venmo, Cash
          App, or PayPal, meeting supporters wherever they prefer to give.
        </>,
        <>
          <span className={'tw-font-bold'}>GoFundMe, Built In:</span> Create a new GoFundMe or link an existing one
          right on your InKind Page, where supporters can give without leaving and follow a live tracker.
        </>,
        <>
          <span className={'tw-font-bold'}>Highlight Multiple Fundraising Efforts:</span> Clearly communicate additional
          fundraising needs or opportunities in your page&apos;s Special Notes section.
        </>,
        <>
          <span className={'tw-font-bold'}>Collect Gift Cards for Flexible Support:</span> Offer supporters the option
          to donate gift cards, providing versatile, meaningful contributions tailored to recipients’ evolving needs.
        </>,
      ],
    },
  },
  ctaSection: {
    ctas: [
      {
        title: 'Built-In Fundraising',
        content:
          'Give InKind and GoFundMe have teamed up. Start or connect a GoFundMe right on your InKind Page, so financial support sits alongside every other way your community shows up.',
        icon: `${getAssetsUrl()}landing-pages/features/Fundraising-Feature01.png`,
        buttonTitle: 'See How',
        buttonLink: '/articles/gofundme-partnership',
      },
      {
        title: 'Common Questions',
        content:
          'Want to learn more about how Give InKind works? Browse our common questions and learn if a Give InKind Page can help meet your fundraising needs.',
        icon: `${getAssetsUrl()}landing-pages/features/Fundraising-Feature02.png`,
        buttonTitle: 'Read More',
        buttonLink: routes.commonQuestions,
      },
      {
        title: 'For Non-Profits & Organizations',
        content:
          'Give InKind adds new and meaningful ways to engage your donors. Streamline support through digital gifts for distribution and more.',
        icon: `${getAssetsUrl()}landing-pages/features/Fundraising-Feature03.png`,
        buttonTitle: 'Learn More',
        buttonLink: routes.forNonProfits,
      },
    ],
  },
};
